import type { Pipeline } from '@/lib/crm/hooks/usePipelines';

/**
 * Terminal stage names for the given pipelines, read authoritatively from the
 * `is_terminal` wire field (no property reads, no heuristic). A stage is
 * terminal iff `stage.is_terminal === true` — the strict check treats a stale
 * cached payload that lacks the key as non-terminal (empty=false semantics)
 * without throwing.
 *
 * Per pipeline, terminal stages are ordered ascending by `properties.order`;
 * names are `stage.label || stage.stage_name`; blank names are skipped; names
 * are deduped across pipelines preserving first occurrence.
 */
export function selectTerminalStageNames(pipelines: Pipeline[]): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const pipeline of pipelines) {
    const terminal = pipeline.stages
      .filter(stage => stage.is_terminal === true)
      .slice()
      // Guarded like the old `stage.properties?.order`: a stage with a missing
      // or null `properties` must not throw inside the render-time useMemo.
      .sort((a, b) => (a.properties?.order ?? 0) - (b.properties?.order ?? 0));
    for (const stage of terminal) {
      const name = stage.label || stage.stage_name;
      if (name.trim().length === 0) {
        continue;
      }
      if (seen.has(name)) {
        continue;
      }
      seen.add(name);
      names.push(name);
    }
  }
  return names;
}
